import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';

export const OTHER_CREW_VALUE = '__OTHER__';

export const formatCrewDisplayName = (person) => {
  if (typeof person === 'string') return person.trim();
  if (!person || typeof person !== 'object') return '';
  const fn = String(person.firstName || '').trim();
  const ln = String(person.lastName || '').trim();
  return [fn, ln].filter(Boolean).join(' ');
};

const normalizeNameList = (names) => {
  const out = [];
  const seen = new Set();
  (names || []).forEach((n) => {
    const label = formatCrewDisplayName(n);
    if (!label || seen.has(label)) return;
    seen.add(label);
    out.push(label);
  });
  return out;
};

const buildCrewOptions = (allCrew, excludeName) => {
  const exclude = String(excludeName || '').trim();
  const out = [];
  const seen = new Set();
  (allCrew || []).forEach((member) => {
    const label = formatCrewDisplayName(member);
    if (!label || label === exclude || seen.has(label)) return;
    seen.add(label);
    out.push(label);
  });
  return out.sort((a, b) => a.localeCompare(b));
};

/**
 * PIC / Co-Pilot picker: ops-assigned crew first, "Other" opens full client crew list.
 */
const TlbCrewNameSelect = ({
  label,
  value = '',
  onChange,
  assignedPilots = [],
  allCrew = [],
  otherPicName = null,
  disabled = false,
  allowEmpty = false
}) => {
  const trimmedValue = String(value || '').trim();
  const assignedNames = useMemo(
    () => normalizeNameList(assignedPilots).filter((n) => n !== String(otherPicName || '').trim()),
    [assignedPilots, otherPicName]
  );
  const crewLabels = useMemo(
    () => buildCrewOptions(allCrew, otherPicName),
    [allCrew, otherPicName]
  );
  const crewLabelsWithCurrent = useMemo(() => {
    if (trimmedValue && !crewLabels.includes(trimmedValue)) {
      return [...crewLabels, trimmedValue].sort((a, b) => a.localeCompare(b));
    }
    return crewLabels;
  }, [crewLabels, trimmedValue]);

  const hasAssignedGroup = assignedNames.length > 0;
  const valueInAssigned = Boolean(trimmedValue && assignedNames.includes(trimmedValue));
  const [otherMode, setOtherMode] = useState(false);

  useEffect(() => {
    if (!hasAssignedGroup) {
      setOtherMode(false);
      return;
    }
    if (valueInAssigned) {
      setOtherMode(false);
      return;
    }
    if (trimmedValue) {
      setOtherMode(true);
    }
  }, [hasAssignedGroup, valueInAssigned, trimmedValue]);

  const showOtherPicker = hasAssignedGroup && otherMode;

  const primaryValue = (() => {
    if (!hasAssignedGroup) return trimmedValue;
    if (otherMode) return OTHER_CREW_VALUE;
    if (valueInAssigned) return trimmedValue;
    return allowEmpty ? '' : '';
  })();

  const handlePrimaryChange = (event) => {
    const next = event.target.value;
    if (next === OTHER_CREW_VALUE) {
      setOtherMode(true);
      if (valueInAssigned) onChange('');
      return;
    }
    if (next === '') {
      setOtherMode(false);
      onChange('');
      return;
    }
    setOtherMode(false);
    onChange(next);
  };

  const handleFullCrewChange = (event, newValue) => {
    onChange(typeof newValue === 'string' ? newValue : '');
  };

  const handleOtherCrewChange = (event, newValue) => {
    onChange(typeof newValue === 'string' ? newValue : '');
  };

  if (!hasAssignedGroup) {
    const standaloneLabels = (() => {
      if (trimmedValue && !crewLabelsWithCurrent.includes(trimmedValue)) {
        return [...crewLabelsWithCurrent, trimmedValue].sort((a, b) => a.localeCompare(b));
      }
      return crewLabelsWithCurrent;
    })();

    return (
      <Autocomplete
        options={standaloneLabels}
        value={trimmedValue || null}
        onChange={handleFullCrewChange}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={standaloneLabels.length ? 'Select crew member' : 'No crew available'}
            fullWidth
          />
        )}
      />
    );
  }

  return (
    <Box>
      <FormControl fullWidth disabled={disabled} sx={{ mb: showOtherPicker ? 1.5 : 0 }}>
        <InputLabel id={`${label}-primary-label`}>{label}</InputLabel>
        <Select
          labelId={`${label}-primary-label`}
          label={label}
          value={primaryValue}
          onChange={handlePrimaryChange}
        >
          {allowEmpty && (
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
          )}
          <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem' }}>
            Assigned by Operations
          </MenuItem>
          {assignedNames.map((name) => (
            <MenuItem key={name} value={name} sx={{ pl: 3 }}>
              {name}
            </MenuItem>
          ))}
          <MenuItem value={OTHER_CREW_VALUE} sx={{ fontStyle: 'italic' }}>
            Other…
          </MenuItem>
        </Select>
      </FormControl>
      {showOtherPicker && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Select from all crew
          </Typography>
          <Autocomplete
            options={crewLabelsWithCurrent}
            value={trimmedValue || null}
            onChange={handleOtherCrewChange}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${label} (other)`}
                placeholder="Search crew list"
                fullWidth
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default TlbCrewNameSelect;
